import { gql } from '@apollo/client';
import { useDisclosure } from '@dwarvesf/react-hooks';
import { Box, Typography } from '@mui/material';
import {
  GetPaginatedEmergingCommunitiesArgs,
  useGetEmergingCommunitiesForSlCommunityCardQuery,
} from 'graphql/generated';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';
import {
  COMMUNITY_MODAL_TYPES,
  SocialListeningCommunityDialog,
} from './SocialListeningCommunityDialog';

// eslint-disable-next-line
gql`
  query GetEmergingCommunitiesForSLCommunityCard(
    $input: GetPaginatedEmergingCommunitiesArgs!
  ) {
    getEmergingCommunities(input: $input) {
      data {
        community
        emoji
        percentageChange
        postCount
      }
    }
  }
`;

interface Props {
  brandId: string;
}

export const SocialListeningCommunityCard = ({ brandId }: Props) => {
  const communityDialog = useDisclosure();

  const [searchParams] = useSearchParams();
  const modalType = searchParams.get('modalType');

  const communityInput: GetPaginatedEmergingCommunitiesArgs = useMemo(
    () => ({
      filters: {
        dateRange: {
          startDate: moment().subtract(30, 'days').toDate(),
          endDate: moment().toDate(),
        },
        brandId,
      },
    }),
    [brandId],
  );

  const { data } = useGetEmergingCommunitiesForSlCommunityCardQuery({
    variables: {
      input: communityInput,
    },
  });

  const communities = data?.getEmergingCommunities.data || [];

  useEffect(() => {
    if (
      modalType &&
      Object.values(COMMUNITY_MODAL_TYPES).includes(
        modalType as COMMUNITY_MODAL_TYPES,
      )
    ) {
      communityDialog.onOpen();
    }
  }, [modalType]); // eslint-disable-line

  return (
    <>
      <Box
        sx={{
          p: 4,
          borderRadius: 3,
          background: 'rgba(230, 221, 237, 0.50)',
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          flex: 1,
          cursor: 'pointer',
        }}
        onClick={communityDialog.onOpen}
      >
        <Typography
          variant="headline-xs"
          color={theme.colors?.utility['purple-4']}
        >
          Emerging communities
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxHeight: 172,
            overflowX: 'auto',
            height: '100%',
            '&::-webkit-scrollbar': {
              width: 0,
            },
          }}
        >
          {communities.length ? (
            communities.map((community) => {
              const isPositive = community.percentageChange >= 0;
              const color = isPositive
                ? theme.colors?.utility['green-3']
                : theme.colors?.utility['pink-3'];
              return (
                <Box
                  key={community.community}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: theme.colors?.utility['purple-1'],
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        ...theme.typography['subhead-lg'],
                        borderRadius: 1,
                        background: 'rgba(35, 6, 3, 0.05)',
                        width: 26,
                        height: 26,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {community.emoji}
                    </Box>
                    <Typography variant="subhead-xl">
                      {community.community}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography
                      variant="subhead-xl"
                      color={theme.colors?.utility[700]}
                    >
                      {formatBigNumber(community.postCount)} posts
                    </Typography>

                    <Typography variant="headline-sm" color={color}>
                      {isPositive ? '↑' : '↓'}
                      {community.percentageChange.toFixed(2).replace('-', '')}%
                    </Typography>
                  </Box>
                </Box>
              );
            })
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                height: '100%',
              }}
            >
              🫣
              <Typography
                variant="subhead-lg"
                color={theme.colors?.utility[700]}
              >
                Not enough tea yet.
                <br />
                We're still watching.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {communityDialog.isOpen && (
        <SocialListeningCommunityDialog
          onClose={communityDialog.onClose}
          brandId={brandId}
        />
      )}
    </>
  );
};
